// SPDX-License-Identifier: Apache-2.0

// SPA fetch wrapper that injects the active-workspace `X-Workspace-Id`
// header on every call to the local MCP server.
//
// Why:
//   The MCP server's HTTP routes are wrapped by `workspaceMiddleware` which
//   reads `X-Workspace-Id`, resolves the per-workspace vault folder, and
//   binds it into an AsyncLocalStorage scope. The Proxy on `toolOpts.vaultPath`
//   then returns the per-request value to every tool handler.
//
// Usage:
//   import { api } from "../lib/api";
//   const r = await api("/api/brain/chat", { method: "POST", body: ... });
//
// The header is best-effort: if the WorkspacesManager hasn't been initialised
// yet (very early boot, before any user interaction) we fall back to
// `default`, which the server treats as "use $MARKVIEW_VAULT_PATH or
// ~/brain". That mirrors the single-workspace behaviour and never throws.

import { getWorkspacesManager } from "@tether/storage";

const ACTIVE_WORKSPACE_LS_KEY = "tether.activeWorkspaceId";

/**
 * Returns true when the SPA is running on an origin that has the local MCP
 * server reachable at `/api/*`. The MCP server is local-only by design
 * (runs `claude -p` as a subprocess on the user's machine); when the SPA is
 * deployed to Cloudflare Pages or any other static host, the `/api/*` paths
 * 404/405 with HTML responses, which spam the console with EventSource MIME
 * errors and `fetch failed` retries.
 *
 * Heuristic: localhost / 127.0.0.1 / *.local hosts have a local MCP; any
 * `*.pages.dev` / `*.workers.dev` / public domain does NOT. Override with
 * `VITE_MCP_BASE` to force enable for tunneled setups (cloudflared, ngrok).
 */
export function isMcpReachable(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((import.meta as any)?.env?.VITE_MCP_BASE) return true;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local") ||
    host.endsWith(".localhost")
  );
}

/**
 * Read the active workspace id without forcing a manager init. We try the
 * manager first (canonical source of truth) and fall back to the localStorage
 * key it persists to. This keeps the call synchronous + cheap.
 */
function readActiveWorkspaceId(): string {
  try {
    const mgr = getWorkspacesManager();
    const id = mgr.getActiveId();
    if (id) return id;
  } catch {
    /* manager not constructible (SSR / test env) — fall through */
  }
  try {
    const ls = globalThis.localStorage?.getItem(ACTIVE_WORKSPACE_LS_KEY);
    if (ls) return ls;
  } catch {
    /* private mode / SSR */
  }
  return "default";
}

/**
 * Fetch wrapper that injects `X-Workspace-Id` on every call. Passes through
 * any other RequestInit fields (headers, body, signal, …) untouched.
 *
 * Intentional: we DO clone the headers into a fresh `Headers` object so the
 * caller can keep using a plain object literal without us mutating it.
 */
export async function api(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  if (!isMcpReachable()) {
    // No backend on this origin — synthesize a 503 with a JSON body so any
    // caller that does `.json()` on the response gets a parsable shape
    // instead of a SyntaxError. The status itself is what callers should
    // branch on; the body is informative.
    return new Response(
      JSON.stringify({ ok: false, error: "mcp_unreachable", reason: "MCP server is local-only; not deployed to this origin." }),
      { status: 503, headers: { "content-type": "application/json" } },
    );
  }
  const headers = new Headers(init.headers ?? undefined);
  headers.set("X-Workspace-Id", readActiveWorkspaceId());
  return fetch(path, { ...init, headers });
}

/** Synchronous accessor for non-fetch consumers (e.g. WS hello frames). */
export function getActiveWorkspaceIdHeader(): string {
  return readActiveWorkspaceId();
}
